import { ApiStatus } from '@/apis/constants/apiStatus'
import { getLinkedInAuthorizationUrl } from '@/apis/courseApis'
import Button from '@/common/button'
import Image from '@/common/Image'
import { ToastNotify } from '@/common/toastManager'
import { convertPdfPageToPng } from '@/helpers/files'
import { Storage } from '@/helpers/Storage'
import { isValidHttpUrl } from '@/helpers/strings'
import { errorHandlerDisplay, withAsync } from '@/helpers/withAsync'
import useQueryParams from '@/hooks/useQueryParams'
import PdfPreview from '@/pages/course/components/PdfPreview'
import { useGetAttemptedCourseDetailQuery } from '@/store/courseSlice'
import { useAppSelector } from '@/store/hooks'
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
export const CERTIFICATION_LINKEDIN_KEY = 'CERTIFICATION_LINKEDIN_KEY'
const CertificateComplete: FC<{
  setCertificateState: Dispatch<SetStateAction<ApiStatus>>
}> = ({ setCertificateState }) => {
  const { course, courseId } = useQueryParams()
  const [image, setImage] = useState('')
  const [useIframe, setUseIframe] = useState(false)
  const accessToken = useAppSelector((state) => state.auth.access)
  const [isGettingLinkedInUrl, setIsGettingLinkedInUrl] = useState(false)
  const { data: fullCourse } = useGetAttemptedCourseDetailQuery(
    parseInt(course),
    { skip: !course, refetchOnFocus: true, refetchOnMountOrArgChange: true }
  )
  useEffect(() => {
    if (!fullCourse) return
    if (!fullCourse.certificate_url) {
      return setCertificateState('IDLE')
    }
    convertPdfPageToPng(fullCourse?.certificate_url || '')
      .then((cert) => {
        setImage(cert)
        setUseIframe(false)
      })
      .catch((err) => {
        // ToastNotify('info', { message: "Couldn't convert pdf to png" })
        setUseIframe(true)
      })
  }, [fullCourse, setCertificateState])

  const startAuthorizationProcess = async () => {
    setIsGettingLinkedInUrl(true)
    const { error, response } = await withAsync(() =>
      getLinkedInAuthorizationUrl()
    )
    if (error) {
      setIsGettingLinkedInUrl(false)
      return errorHandlerDisplay(error)
    }
    setIsGettingLinkedInUrl(false)
    if (response && isValidHttpUrl(response.data.url)) {
      Storage.setItem(CERTIFICATION_LINKEDIN_KEY, {
        accessToken,
        course,
        courseId,
      })
      window.open(response.data.url, '_blank', 'noopener,noreferrer')
    } else {
      return ToastNotify('info', { message: 'Could not connect to linkedIn.' })
    }
  }

  return (
    <section className='flex w-full p-2.5 flex-col justify-center items-center gap-3'>
      {useIframe && !fullCourse?.certificate_image_url ? (
        <PdfPreview
          title={(fullCourse?.course.title || '') + ' certificate'}
          key={fullCourse?.id}
          url={`${fullCourse?.certificate_url}#toolbar=0`}
          height='100%'
          className='aspect-[11/8] rounded-3xl'
        />
      ) : (
        <Image
          alt='Certificate'
          src={fullCourse?.certificate_image_url || image}
          className=' rounded-3xl aspect-[11/8] bg-jaa-shades-bg shadow-sm w-full object-cover object-center'
        />
      )}
      <footer className='w-full mt-8 flex justify-center items-center'>
        <Button
          variant='empty'
          onClick={startAuthorizationProcess}
          loading={isGettingLinkedInUrl}
          disabled={isGettingLinkedInUrl}
          label={
            <>
              <span className='text-[#5a6d72] text-base font-semibold leading-normal text-center'>
                Share to LinkedIn
              </span>
              <svg
                width='25'
                height='25'
                viewBox='0 0 25 25'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M22.25 1.01962H2.75C2.30756 1.01512 1.8814 1.18631 1.56504 1.49565C1.24867 1.80498 1.06794 2.22719 1.0625 2.66962V22.2484C1.06892 22.6901 1.25009 23.1114 1.56634 23.4199C1.8826 23.7284 2.3082 23.8991 2.75 23.8946H22.25C22.6925 23.8981 23.1183 23.7263 23.4345 23.4168C23.7507 23.1073 23.9316 22.6852 23.9375 22.2427V2.66399C23.9296 2.22286 23.7479 1.80267 23.4319 1.49475C23.1159 1.18684 22.6912 1.01609 22.25 1.01962Z'
                  fill='#0076B2'
                />
                <path
                  d='M4.44871 9.59375H7.84434V20.5194H4.44871V9.59375ZM6.14746 4.15625C6.53692 4.15625 6.91764 4.27176 7.24144 4.48817C7.56523 4.70459 7.81757 5.01217 7.96653 5.37203C8.11548 5.73188 8.15436 6.12782 8.07824 6.50977C8.00213 6.89173 7.81444 7.24252 7.53891 7.51778C7.26339 7.79304 6.91242 7.9804 6.5304 8.05615C6.14837 8.1319 5.75246 8.09265 5.39275 7.94335C5.03304 7.79405 4.7257 7.54142 4.50959 7.21741C4.29349 6.89341 4.17834 6.51259 4.17871 6.12313C4.17921 5.60131 4.38685 5.10103 4.75601 4.73222C5.12517 4.36341 5.62564 4.15625 6.14746 4.15625ZM9.97434 9.59375H13.2293V11.0938H13.2743C13.7281 10.235 14.8343 9.32938 16.4862 9.32938C19.925 9.32188 20.5625 11.585 20.5625 14.5194V20.5194H17.1668V15.2038C17.1668 13.9381 17.1443 12.3088 15.4025 12.3088C13.6606 12.3088 13.3643 13.6888 13.3643 15.1213V20.5194H9.97434V9.59375Z'
                  fill='white'
                />
              </svg>
            </>
          }
        />
      </footer>
    </section>
  )
}

export default CertificateComplete
